import React, { useRef, useEffect, useCallback } from 'react';

const AnalogWatch = () => {
    const canvasRef = useRef(null);
    const animationFrameRef = useRef(null);

    // Flytt tegne-funksjonene ut av useEffect for bedre ytelse
    const drawFace = useCallback((ctx, radius) => {
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.9, 0, 2 * Math.PI);
        ctx.fillStyle = '#0d0d0d';
        ctx.fill();

        ctx.strokeStyle = '#388bfd';
        ctx.lineWidth = radius * 0.02;
        ctx.stroke();

        for (let i = 0; i < 12; i++) {
            const ang = (i * Math.PI) / 6;
            ctx.beginPath();
            ctx.rotate(ang);
            ctx.moveTo(radius * 0.85, 0);
            ctx.lineTo(radius * 0.89, 0);
            ctx.strokeStyle = '#388bfd';
            ctx.lineWidth = radius * 0.02;
            ctx.lineCap = 'round';
            ctx.stroke();
            ctx.rotate(-ang);
        }

        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.04, 0, 2 * Math.PI);
        ctx.fillStyle = '#388bfd';
        ctx.fill();
    }, []);

    const drawNumbers = useCallback((ctx, radius) => {
        ctx.font = `${radius * 0.15}px 'Inter', sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#ffffff';

        for (let num = 1; num <= 12; num++) {
            const ang = (num * Math.PI) / 6;
            const x = Math.sin(ang) * (radius * 0.75);
            const y = -Math.cos(ang) * (radius * 0.75);
            ctx.fillText(num.toString(), x, y);
        }
    }, []);

    const drawHand = useCallback((ctx, pos, length, width) => {
        ctx.beginPath();
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        ctx.moveTo(0, 0);
        ctx.rotate(pos);
        ctx.lineTo(0, -length);
        ctx.stroke();
        ctx.rotate(-pos);
    }, []);

    const drawTime = useCallback((ctx, radius) => {
        const now = new Date();
        let hour = now.getHours();
        let minute = now.getMinutes();
        let second = now.getSeconds();

        hour = hour % 12;
        hour = (hour * Math.PI) / 6 + (minute * Math.PI) / (6 * 60);
        ctx.strokeStyle = '#ffffff';
        drawHand(ctx, hour, radius * 0.5, radius * 0.03);

        minute = (minute * Math.PI) / 30;
        ctx.strokeStyle = '#ffffff';
        drawHand(ctx, minute, radius * 0.7, radius * 0.025);

        second = (second * Math.PI) / 30;
        ctx.strokeStyle = '#388bfd';
        drawHand(ctx, second, radius * 0.8, radius * 0.01);
    }, []);

    // Optimaliser hoved-tegnerutinen
    const drawClock = useCallback((ctx, radius, dpr) => {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        // Center using CSS pixels (rect dimensions) rather than scaled values
        ctx.translate(radius, radius);
        
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        
        drawFace(ctx, radius);
        drawNumbers(ctx, radius);
        drawTime(ctx, radius);
    }, [drawFace, drawNumbers, drawTime]);

    // Initialisering og animasjonsloop
    useEffect(() => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d', { alpha: false });
        
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const radius = rect.width / 2;

        const animate = () => {
            drawClock(context, radius, dpr);
            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [drawClock]);

    return (
        <div style={styles.container}>
            <canvas ref={canvasRef} style={styles.canvas} />
        </div>
    );
};

// Flytt styles ut av komponenten
const styles = {
    container: {
        backgroundColor: '#0d0d0d',
        padding: '20px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        width: 'fit-content',
        margin: 'auto'
    },
    canvas: {
        backgroundColor: '#0d0d0d',
        borderRadius: '50%',
        border: '1px solid #388bfd',
        width: '290px',
        height: '290px',
        display: 'block'
    }
};

export default React.memo(AnalogWatch);
